import { FC, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Image, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Spinner";
import { note } from "@/endpoints/dryp/note";

type PhotoGalleryModalProps = {
	photos: Dryp.Note[];
	onClose: () => void;
};

type Status = "loading" | "error";

export const PhotoGalleryModal: FC<PhotoGalleryModalProps> = ({ photos, onClose }) => {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
	const [status, setStatus] = useState<Record<string, Status>>({});

	const currentPhoto = photos[currentIndex];
	const currentImageUrl = currentPhoto ? imageUrls[currentPhoto.id] : null;
	const currentStatus = currentPhoto ? status[currentPhoto.id] : undefined;

	useEffect(() => {
		if (!currentPhoto) return;

		const photoId = currentPhoto.id;
		if (imageUrls[photoId] || status[photoId]) return;

		setStatus(prev => ({ ...prev, [photoId]: "loading" }));

		note.getNoteMediaFile(photoId)
			.then(blob => {
				setImageUrls(prev => ({ ...prev, [photoId]: URL.createObjectURL(blob) }));
				setStatus(prev => {
					const { [photoId]: _, ...rest } = prev;
					return rest;
				});
			})
			.catch(() => {
				setStatus(prev => ({ ...prev, [photoId]: "error" }));
			});
	}, [currentPhoto?.id, imageUrls, status]);

	useEffect(() => {
		return () => {
			Object.values(imageUrls).forEach(url => URL.revokeObjectURL(url));
		};
	}, []);

	const goToPrevious = useCallback(() => {
		setCurrentIndex(prev => (prev > 0 ? prev - 1 : photos.length - 1));
	}, [photos.length]);

	const goToNext = useCallback(() => {
		setCurrentIndex(prev => (prev < photos.length - 1 ? prev + 1 : 0));
	}, [photos.length]);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
			if (e.key === "ArrowLeft") goToPrevious();
			if (e.key === "ArrowRight") goToNext();
		};
		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [onClose, goToPrevious, goToNext]);

	const formatDate = (timestamp: number) => {
		return new Date(timestamp).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	const getThumbnailSrc = (photo: Dryp.Note) => {
		if (!photo.thumbnail) return null;
		return photo.thumbnail.startsWith("data:") ? photo.thumbnail : `data:image/jpeg;base64,${photo.thumbnail}`;
	};

	const modalContent = (
		<div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80" onClick={onClose}>
			<div
				className="relative w-[95vw] max-w-6xl h-[92vh] rounded-lg overflow-hidden bg-card flex flex-col"
				onClick={e => e.stopPropagation()}
			>
				{/* Header */}
				<div className="p-4 border-b border-border flex items-center justify-between shrink-0">
					<div className="flex items-center gap-2">
						<Image className="w-5 h-5" />
						<h3 className="font-semibold">Structure Photos</h3>
						<span className="text-sm text-muted-foreground">
							{currentIndex + 1} / {photos.length}
						</span>
					</div>
					<Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
						<X className="w-4 h-4" />
					</Button>
				</div>

				{/* Main Image Display */}
				<div className="flex-1 p-6 flex flex-col overflow-hidden">
					<div className="flex-1 relative bg-secondary rounded-lg overflow-hidden flex items-center justify-center mb-4">
						{currentStatus === "loading" ? (
							<Spinner messages={[]} size={48} />
						) : currentStatus === "error" ? (
							<div className="flex flex-col items-center gap-2 text-muted-foreground">
								<AlertCircle className="w-12 h-12" />
								<p className="text-sm">Failed to load image</p>
							</div>
						) : currentImageUrl ? (
							<img
								src={currentImageUrl}
								alt={currentPhoto?.description || "Photo"}
								className="max-w-full max-h-full object-contain"
							/>
						) : (
							<Spinner messages={[]} size={48} />
						)}

						{/* Navigation Arrows */}
						{photos.length > 1 && (
							<>
								<button
									onClick={goToPrevious}
									className="absolute left-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-background/80 hover:bg-background flex items-center justify-center"
								>
									<ChevronLeft className="w-5 h-5" />
								</button>
								<button
									onClick={goToNext}
									className="absolute right-4 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-background/80 hover:bg-background flex items-center justify-center"
								>
									<ChevronRight className="w-5 h-5" />
								</button>
							</>
						)}
					</div>

					{/* Caption */}
					{currentPhoto && (
						<div className="text-center mb-4 shrink-0">
							{currentPhoto.description && (
								<p className="text-sm text-muted-foreground">{currentPhoto.description}</p>
							)}
							<p className="text-xs text-muted-foreground mt-1">
								{currentPhoto.createdBy}, {formatDate(currentPhoto.createdAt)}
							</p>
						</div>
					)}

					{/* Thumbnail Strip */}
					{photos.length > 1 && (
						<div className="flex gap-2 justify-center overflow-x-auto pb-2 shrink-0">
							{photos.map((photo, index) => {
								const thumbSrc = getThumbnailSrc(photo);
								const isActive = index === currentIndex;

								return (
									<button
										key={photo.id}
										onClick={() => setCurrentIndex(index)}
										className={`w-16 h-16 rounded overflow-hidden border-2 flex-shrink-0 transition-all ${
											isActive
												? "border-primary"
												: "border-transparent opacity-60 hover:opacity-100"
										}`}
									>
										{thumbSrc ? (
											<img src={thumbSrc} alt="" className="w-full h-full object-cover" />
										) : (
											<div className="w-full h-full bg-secondary flex items-center justify-center">
												<Image className="w-6 h-6 text-muted-foreground" />
											</div>
										)}
									</button>
								);
							})}
						</div>
					)}
				</div>
			</div>
		</div>
	);

	return createPortal(modalContent, document.body);
};
